import { NgModule, ModuleWithProviders, ErrorHandler } from '@angular/core';
import { HTTP_INTERCEPTORS, HttpErrorResponse } from '@angular/common/http';
import { SENTRY_INTERCEPTOR_CONFIG } from './config';
import { ErrorFilter } from './error.filter';
import { SentryInterceptor } from './interceptor';
import { ConfigService } from '@jms/ui/ui.standalone.services/config.service';;

import * as Sentry from '@sentry/browser';
import { JwtHelperService } from '@auth0/angular-jwt';
import { TokenUser } from '@jms/model';

export interface SentryInterceptorConfig {
    blacklistCodes?: number[];
    whitelistCodes?: number[];
}

/**
 * HTTP statuses the interceptor never reports. None indicate a frontend defect:
 * 0 is network loss or a tab closing mid-request, 401 is session expiry,
 * 403 is a permission denial, 409 is a deliberate concurrency conflict from the API.
 */
export const SENTRY_IGNORED_HTTP_STATUSES: number[] = [0, 401, 403, 409];

const environment = ConfigService.environment;

Sentry
    .init({
        dsn: environment.sentry.dsn,
        environment: environment.sentry.environment,
        release: environment.build ? environment.build : 'DEV',
        enabled: environment.sentry.environment !== 'dev'
    });

/** Server error bodies can be arbitrarily large; keep Sentry extras readable. */
const SENTRY_ERROR_BODY_MAX_LENGTH = 500;

export class SentryErrorHandler implements ErrorHandler {


    constructor(
    ) { }

    handleError(err: unknown): void {
        console.error(err);

        const response = this.asHttpErrorResponse(err);

        if (response == null) {
            Sentry.captureException(err, {
                user: {
                    email: this?.user?.email
                }
            });
            return;
        }

        if (SENTRY_IGNORED_HTTP_STATUSES.includes(response.status)) {
            return;
        }

        const url = response.url ?? 'unknown url';
        const error = new Error(`request to ${url} failed with ${response.status}.`);
        error.name = 'HttpError';

        Sentry.captureException(error, {
            user: {
                email: this?.user?.email
            },
            extra: {
                status: response.status,
                statusText: response.statusText,
                url: response.url,
                message: response.message,
                error: this.describeErrorBody(response.error)
            }
        });
    }

    /**
     * `instanceof` alone is unreliable when more than one copy of @angular/common/http
     * is bundled, so fall back to the runtime name Angular stamps on the response.
     */
    private asHttpErrorResponse(err: unknown): HttpErrorResponse {
        if (err instanceof HttpErrorResponse) {
            return err;
        }

        const candidate = err as HttpErrorResponse;

        return candidate?.name === 'HttpErrorResponse'
            ? candidate
            : null;
    }

    private describeErrorBody(body: unknown): string {
        if (body == null) {
            return null;
        }

        const text = typeof body === 'string'
            ? body
            : this.stringifyErrorBody(body);

        return text.length > SENTRY_ERROR_BODY_MAX_LENGTH
            ? `${text.slice(0, SENTRY_ERROR_BODY_MAX_LENGTH)}…`
            : text;
    }

    private stringifyErrorBody(body: unknown): string {
        try {
            return JSON.stringify(body);
        } catch {
            return String(body);
        }
    }

    private get user(): TokenUser {
        const token = localStorage.getItem('auth_token');

        const jwt = new JwtHelperService({});
        const user = token
            ? jwt.decodeToken(token)
            : null;

        return user;
    }
}

@NgModule({})
export class SentryModule {

    public static forRoot(config: SentryInterceptorConfig = {}): ModuleWithProviders<SentryModule> {

        return {
            ngModule: SentryModule,
            providers: [
                {
                    provide: SENTRY_INTERCEPTOR_CONFIG,
                    useValue: config
                },
                {
                    provide: HTTP_INTERCEPTORS,
                    useClass: SentryInterceptor,
                    multi: true
                },
                {
                    provide: ErrorHandler,
                    useClass: SentryErrorHandler
                },
                ErrorFilter,
            ]
        };
    }
}
